import { useMemo } from 'react';
import Image from 'next/legacy/image';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import { Row, Col, Form, Button } from 'antd';

import { IContact } from '../../common/model/mailchimp/contact.model';
import useI18n from '../../common/hooks/useI18n';

import { ContactForm } from '../shared';

type Props = {
  query: string;
  onContactButtonClick: (contact: IContact) => void;
  theme: DefaultTheme;
};

const Background = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  padding-top: 136px;
  padding-bottom: 136px;
  padding-left: ${(props) => props.theme.grid.getGridColumns(3, 1)};
  padding-right: ${(props) => props.theme.grid.getGridColumns(3, 1)};

  @media ${(props) => props.theme.breakpoints.smd} {
    padding-top: 80px;
    padding-bottom: 80px;
    padding-left: ${(props) => props.theme.grid.getGridColumns(2, 1)};
    padding-right: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  }
`;

const ImageAbsoluteContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: -1;
`;

const ImageContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const Card = styled.div`
  border-radius: ${(props) => props.theme.borderRadius};
  background-color: rgba(255, 255, 255, 0.9);
  box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.1);
  padding: 64px;
  @media ${(props) => props.theme.breakpoints.mdd} {
    padding: 40px;
  }
  @media ${(props) => props.theme.breakpoints.xs} {
    padding: 32px;
  }
`;

const Title = styled.h1`
  ${(props) => props.theme.font.h2}
  color: ${(props) => props.theme.colors.secondary};
  line-height: 125% !important;
`;

const Divider = styled.div`
  border: 1px solid ${(props) => props.theme.colors.primary};
  background-color: ${(props) => props.theme.colors.primary};
  border-radius: ${(props) => props.theme.borderRadius};

  width: 64px;
  margin-top: 24px;
  margin-bottom: 24px;
`;

const Subtitle = styled.div`
  color: ${(props) => props.theme.colors.secondary};
  font-size: 18px;
  line-height: 27px;
  font-weight: 500;

  @media ${(props) => props.theme.breakpoints.lgd} {
    line-height: 22px;
  }

  @media ${(props) => props.theme.breakpoints.smd} {
    font-size: 14px;
  }
`;

const SellHouseHero: React.FC<Props> = ({
  query,
  onContactButtonClick,
  theme,
}) => {
  const i18n = useI18n();
  const [form] = Form.useForm();

  const title = useMemo(() => i18n.t(`dynamic.sellHousePage.${query}.title`), [
    i18n,
    query,
  ]);
  const subtitle = useMemo(
    () => i18n.t(`dynamic.sellHousePage.${query}.subtitle`),
    [i18n, query]
  );

  return (
    <Background>
      <ImageAbsoluteContainer>
        <ImageContainer>
          <Image
            alt="sellHouseHero"
            layout="fill"
            objectFit="cover"
            objectPosition="center 32%"
            src="/images/sell_house/hero.jpg"
            sizes={theme.getImageSizes('100vw')}
            priority
          />
        </ImageContainer>
      </ImageAbsoluteContainer>
      <Card>
        <Row gutter={[24, 32]} align="middle">
          <Col xs={24} md={12}>
            <Title
              key={title.substring(0, 10) + new Date().getTime()}
              as="h1"
              dangerouslySetInnerHTML={{
                __html: title,
              }}
            />
            <Divider />
            <Subtitle
              key={subtitle.substring(0, 10) + new Date().getTime()}
              as="div"
              dangerouslySetInnerHTML={{
                __html: subtitle,
              }}
            />
          </Col>
          <Col xs={24} md={12}>
            <ContactForm
              form={form}
              onFinish={onContactButtonClick}
              showAddress={true}
            />
            <Row justify="end">
              <Col>
                <Button
                  type="primary"
                  htmlType="button"
                  onClick={() => form.submit()}
                >
                  {i18n.t(`dynamic.sellHousePage.${query}.mainButton`)}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>
    </Background>
  );
};

export default withTheme(SellHouseHero);
